import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Project, Tag } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TagBadge } from '@/components/shared/TagBadge';
import { useProjectsApi, useTagsApi } from '@/services/api';
import { t } from '@/i18n';

/** Tag-rule edit payload: the desired full set + the set loaded on open. */
export interface ProjectTagRulesValues {
  tagIds: string[];
  initialTagIds: string[];
}

export interface ProjectFormValues {
  name: string;
  code: string;
  description: string;
  color: string;
  /**
   * null when the rules were never loaded (create mode, or the load failed)
   * — the caller MUST NOT touch tag rules then, or it would wipe them.
   */
  tagRules: ProjectTagRulesValues | null;
}

interface ProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyNip: string;
  /** null = create mode, a project = edit mode (form pre-filled). */
  editing: Project | null;
  /** Create-mode default: color maximally distant from the company's projects. */
  suggestedColor: string;
  saving: boolean;
  onSubmit: (values: ProjectFormValues) => void;
}

/** One module group of the tag-rule multi-select (expense / sales). */
function TagRuleGroup({
  label,
  tags,
  selected,
  onToggle,
}: {
  label: string;
  tags: Tag[];
  selected: string[];
  onToggle: (tagId: string, checked: boolean) => void;
}) {
  if (tags.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs font-medium">{label}</p>
      <div className="flex flex-col gap-0.5">
        {tags.map((tag) => (
          <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
            <Checkbox
              checked={selected.includes(tag.id)}
              onCheckedChange={(checked) => onToggle(tag.id, checked === true)}
            />
            <TagBadge name={tag.name} color={tag.color} />
          </Label>
        ))}
      </div>
    </div>
  );
}

/**
 * Task B3: "Nowy projekt" / "Edytuj projekt" dialog. The color picker reuses
 * the native-input + hex-text pattern from TagManagement.
 *
 * Tag rules (auto-assignment by tag) live HERE, in the edit dialog, rather
 * than on the analytics view: this dialog is only reachable for
 * admin/ksiegowy/superadmin — the exact grade the tag-rules endpoints
 * require — while the analytics page is a read-only surface also served to
 * users who would 403 on those endpoints. Saving is orchestrated by the
 * parent (ProjectsView) together with the project fields.
 */
export function ProjectDialog({
  open,
  onOpenChange,
  companyNip,
  editing,
  suggestedColor,
  saving,
  onSubmit,
}: ProjectDialogProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(suggestedColor);

  // Tag rules (edit mode only). initialRuleTagIds === null means "not
  // loaded" — the submit payload then carries tagRules: null (no write).
  const [expenseTags, setExpenseTags] = useState<Tag[]>([]);
  const [salesTags, setSalesTags] = useState<Tag[]>([]);
  const [ruleTagIds, setRuleTagIds] = useState<string[]>([]);
  const [initialRuleTagIds, setInitialRuleTagIds] = useState<string[] | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState(false);

  const { getTags } = useTagsApi();
  const { getTagRules } = useProjectsApi();

  // Re-prime the form each time the dialog opens (create empty / edit filled).
  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '');
      setCode(editing?.code ?? '');
      setDescription(editing?.description ?? '');
      setColor(editing?.color ?? suggestedColor);
    }
  }, [open, editing, suggestedColor]);

  // Load the company's expense + sales tags and the project's current rules
  // when the dialog opens in edit mode.
  useEffect(() => {
    if (!open || !editing) {
      setExpenseTags([]);
      setSalesTags([]);
      setRuleTagIds([]);
      setInitialRuleTagIds(null);
      setRulesError(false);
      return;
    }
    let cancelled = false;
    setRulesLoading(true);
    setRulesError(false);
    setInitialRuleTagIds(null);
    Promise.all([
      getTags(companyNip, 'expense'),
      getTags(companyNip, 'sales'),
      getTagRules(companyNip, editing.id),
    ])
      .then(([expense, sales, rules]) => {
        if (cancelled) return;
        setExpenseTags(expense);
        setSalesTags(sales);
        setRuleTagIds(rules.tagIds);
        setInitialRuleTagIds(rules.tagIds);
      })
      .catch((error) => {
        if (cancelled) return;
        setRulesError(true);
        console.error(error);
      })
      .finally(() => {
        if (!cancelled) setRulesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, editing, companyNip, getTags, getTagRules]);

  const toggleRuleTag = (tagId: string, checked: boolean) => {
    setRuleTagIds((prev) =>
      checked ? [...prev, tagId] : prev.filter((id) => id !== tagId),
    );
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error(t('projects.form.nameRequired'));
      return;
    }
    onSubmit({
      name: name.trim(),
      code: code.trim(),
      description: description.trim(),
      color,
      tagRules:
        initialRuleTagIds !== null
          ? { tagIds: ruleTagIds, initialTagIds: initialRuleTagIds }
          : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('projects.form.editTitle') : t('projects.form.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="project-name" className="text-muted-foreground text-xs">
              {t('projects.form.name')}
            </Label>
            <Input
              id="project-name"
              placeholder={t('projects.form.namePlaceholder')}
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="project-code" className="text-muted-foreground text-xs">
              {t('projects.form.code')}
            </Label>
            <Input
              id="project-code"
              placeholder={t('projects.form.codePlaceholder')}
              value={code}
              maxLength={50}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="project-description" className="text-muted-foreground text-xs">
              {t('projects.form.description')}
            </Label>
            <Textarea
              id="project-description"
              placeholder={t('projects.form.descriptionPlaceholder')}
              value={description}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="project-color" className="text-muted-foreground text-xs">
              {t('projects.form.color')}
            </Label>
            <div className="flex items-center gap-2">
              <input
                id="project-color"
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#000000'}
                onChange={(e) => setColor(e.target.value)}
                className="border-input h-9 w-10 cursor-pointer rounded border p-1"
              />
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-[120px]"
              />
            </div>
          </div>

          {/* Tag rules — edit mode only (a project must exist to have rules). */}
          {editing && (
            <div className="space-y-2 border-t pt-3">
              <div>
                <p className="text-sm font-medium">{t('projects.tagRules.sectionTitle')}</p>
                <p className="text-muted-foreground text-xs">
                  {t('projects.tagRules.description')}
                </p>
              </div>
              {rulesLoading ? (
                <div className="text-muted-foreground flex items-center gap-2 py-2 text-sm">
                  <Loader2 className="size-4 animate-spin" />
                </div>
              ) : rulesError ? (
                <p className="text-destructive text-xs">{t('projects.tagRules.loadError')}</p>
              ) : expenseTags.length === 0 && salesTags.length === 0 ? (
                <p className="text-muted-foreground text-xs">{t('projects.tagRules.noTags')}</p>
              ) : (
                <div className="max-h-56 space-y-3 overflow-y-auto pr-1">
                  <TagRuleGroup
                    label={t('projects.tagRules.expenseGroup')}
                    tags={expenseTags}
                    selected={ruleTagIds}
                    onToggle={toggleRuleTag}
                  />
                  <TagRuleGroup
                    label={t('projects.tagRules.salesGroup')}
                    tags={salesTags}
                    selected={ruleTagIds}
                    onToggle={toggleRuleTag}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {editing ? t('projects.form.submitSave') : t('projects.form.submitCreate')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
